import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { applyTheme, getInitialTheme } from "./lib/theme";
import { applyLangDir, getInitialLang, I18nProvider } from "./lib/i18n";
import "./index.css";

// Apply theme + language/direction before React mounts to avoid a flash of the
// wrong palette or text direction (fa/ar are RTL).
applyTheme(getInitialTheme());
applyLangDir(getInitialLang());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <BrowserRouter basename="/journal">
          <App />
        </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);

// Capture the PWA install prompt as early as possible — the browser fires
// `beforeinstallprompt` once on load (before React may have mounted), so we
// stash the event on window and re-broadcast it as a custom event the
// InstallBanner component listens for. `appinstalled` clears it.
declare global {
  interface Window {
    deferredInstallPrompt?: Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
  }
}
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.deferredInstallPrompt = e as Window["deferredInstallPrompt"];
  window.dispatchEvent(new CustomEvent("pwa-installable"));
});
window.addEventListener("appinstalled", () => {
  window.deferredInstallPrompt = undefined;
  window.dispatchEvent(new CustomEvent("pwa-installed"));
});

// Register the PWA service worker (installable + offline app shell).
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/journal/sw.js", { scope: "/journal/" })
      .catch(() => {
        /* SW is a progressive enhancement — ignore registration failures */
      });
  });
}
